import { ReactNode } from "react";

import {
  CheckRoundIcon,
  ExclamationCircleIcon,
  MicFilledIcon,
  VideoCameraFilledIcon,
  VideoCameraSlashIcon,
} from "@vericus/cadmus-icons";
import { colors } from "@vericus/cadmus-ui";

import { assignInlineVars } from "@vanilla-extract/dynamic";

import { UseWebcamCaptureReturn } from "@/components/webcam-capture";

import * as styles from "./overlay.css";
import { useMicrophoneVolume } from "./use-microphone-volume";

interface OverlayProps {
  webcam: UseWebcamCaptureReturn;
  onToggleCamera?: () => void;
  cameraToggleDisabled?: boolean;
  showVolumeMeter?: boolean;
}

export const Overlay = ({
  webcam,
  onToggleCamera,
  cameraToggleDisabled = false,
  showVolumeMeter = true,
}: OverlayProps) => {
  const stream = webcam.stream;

  const hasAudioTrack =
    stream
      ?.getAudioTracks()
      .some((track) => track.readyState === "live" && track.enabled) ?? false;
  const { volumeLevel } = useMicrophoneVolume(showVolumeMeter ? stream : null);

  const isAudioOn = hasAudioTrack;
  const isVideoOn =
    stream
      ?.getVideoTracks()
      .some((track) => track.readyState === "live" && track.enabled) ?? false;

  return (
    <div className={styles.container}>
      <div className={styles.indicatorsContainer}>
        <div className={styles.indicator({ active: isAudioOn })}>
          <IconWrapper>
            <MicFilledIcon
              label={isAudioOn ? "Microphone on" : "Microphone off"}
              size={20}
              color="white"
            />
          </IconWrapper>
          {isAudioOn && showVolumeMeter && (
            <div className={styles.miniStatus}>
              <CheckRoundIcon label="" size={16} color={colors.success} />
            </div>
          )}
          {!isAudioOn && (
            <div className={styles.miniStatus}>
              <ExclamationCircleIcon label="" size={16} color="#D92644" />
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.indicator({
            active: isVideoOn,
            cameraTurnedOff: webcam.cameraEnabled === false,
            interactive: true,
            disabled: cameraToggleDisabled,
          })}
          onClick={onToggleCamera}
          disabled={cameraToggleDisabled}
          aria-pressed={webcam.cameraEnabled}
          aria-label={
            webcam.cameraEnabled ? "Turn camera off" : "Turn camera on"
          }
        >
          <IconWrapper>
            {webcam.cameraEnabled === false ? (
              <VideoCameraSlashIcon label="" size={20} />
            ) : (
              <VideoCameraFilledIcon
                label=""
                size={20}
                color="white"
                fill="white"
              />
            )}
          </IconWrapper>
          {isVideoOn && showVolumeMeter && (
            <div className={styles.miniStatus}>
              <CheckRoundIcon label="" size={16} color={colors.success} />
            </div>
          )}
          {!isVideoOn && webcam.cameraEnabled !== false && (
            <div className={styles.miniStatus}>
              <ExclamationCircleIcon label="" size={16} color="#D92644" />
            </div>
          )}
        </button>
      </div>
      {showVolumeMeter && (
        <div className={styles.volumeMeterContainer}>
          <div
            className={styles.volumeMeter}
            style={assignInlineVars({
              [styles.volumeScale]: `${volumeLevel * 100}%`,
            })}
          >
            <div className={styles.volumeFill} />
          </div>
          <div className={styles.volumeMeterIcon}>
            <MicFilledIcon label="Microphone level" size={16} color="white" />
          </div>
        </div>
      )}
    </div>
  );
};

const IconWrapper = ({ children }: { children: ReactNode }) => (
  <div className={styles.iconWrapper}>{children}</div>
);
